<template>
  <DataCard
    title="Informations client"
    icon="fas fa-user"
    iconBgColor="bg-blue-100"
    iconColor="text-blue-600"
    :clickable="false"
    className="p-3 max-[600px]:p-4"
  >
    <template #header-toggle v-if="collapsible">
      <button
        type="button"
        class="text-black md:hidden"
        @click.stop="toggleCollapse"
      >
        <i
          :class="[
            'fas transition-transform duration-200',
            isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'
          ]"
        ></i>
      </button>
    </template>
    <!-- Version Desktop -->
    <div v-if="clientData" class="hidden md:block">
      <div class="grid grid-cols-1 gap-2">
        <!-- Client Information -->
        <div>
          <h4 class="font-bold text-black mb-1 text-lg">Informations du client</h4>
          <div class="space-y-0.5 text-sm">
            <div class="flex justify-between">
              <span class="text-black">Numéro de suivi de commande:</span>
              <span class="font-bold text-black">#{{ clientData.orderNumber || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Date de la commande:</span>
              <span class="font-semibold text-black">{{ formatDate(clientData.orderDate) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">NOM Prénom:</span>
              <span class="font-semibold text-black">{{ clientData.fullName || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Email:</span>
              <span class="font-semibold text-black">{{ clientData.email || '-' }}</span>
            </div>
            <div class="flex justify-between items-start gap-2">
              <span class="text-black relative" data-siret-tva-info>
                N° SIRET / TVA:
                <button
                  type="button"
                  class="inline-flex items-center justify-center w-4 h-4 ml-1 text-[10px] font-bold text-blue-600 border border-blue-300 rounded-full hover:bg-blue-50 transition-colors align-middle"
                  @click.stop="showSiretTvaInfo = !showSiretTvaInfo"
                >
                  i
                </button>
                <div
                  v-if="showSiretTvaInfo"
                  class="absolute -right-[260px] bottom-full mb-2 w-[300px] px-4 py-3 bg-blue-50 border border-blue-200 rounded text-sm text-black z-[99999] shadow-lg"
                >
                  <div class="space-y-1">
                    <p>
                    Pour le SIRET :
                    <a
                      href="https://avis-situation-sirene.insee.fr/"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-blue-600 hover:underline"
                    >
                      cliquer ici pour vérifier le SIRET
                    </a>
                    </p>
                    <p>
                    Pour la TVA :
                    <a
                      href="https://ec.europa.eu/taxation_customs/vies/#/vat-validation"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-blue-600 hover:underline"
                    >
                      cliquer ici pour vérifier la TVA
                    </a>
                    </p>
                  </div>
                </div>
              </span>
              <span class="font-semibold text-black text-right">{{ clientData.siret || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">N° EORI:</span>
              <span class="font-semibold text-black">{{ clientData.eori || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Mode de paiement:</span>
              <span class="font-semibold text-black">{{ clientData.paymentMethod || '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Adresse de facturation -->
      <div v-if="billingAddress" class="mt-2">
        <h4 class="font-bold text-black mb-1 text-lg">Adresse de facturation</h4>
        <div class="space-y-0.5 text-sm">
          <div class="flex justify-between">
            <span :class="isAdmin ? 'text-red-600 font-bold text-base' : 'text-black'">Nom de la société:</span>
            <span :class="isAdmin ? 'text-red-600 font-bold text-base' : 'text-black'">{{ billingAddress.enseigne || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-black">Adresse:</span>
            <span class="font-semibold text-black">{{ billingAddress.adresse || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-black">Code postal:</span>
            <span class="font-semibold text-black">{{ billingAddress.codePostal || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-black">Ville:</span>
            <span class="font-semibold text-black">{{ billingAddress.ville || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-black">Pays:</span>
            <span class="font-semibold text-black">{{ billingAddress.pays || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span :class="isAdmin ? 'text-red-600 font-bold text-base' : 'text-black'">Téléphone:</span>
            <span :class="isAdmin ? 'text-red-600 font-bold text-base' : 'text-black'">{{ billingAddress.telephone || '-' }}</span>
          </div>
        </div>
      </div>
      
      <!-- Zone d'upload de facture - Desktop -->
      <UploadZone
        v-if="orderDetailData && orderDetailData.id_commande"
        :order-id="Number(orderDetailData.id_commande)"
        :order-group-id="Number(orderDetailData.id_commande_groupe || 0)"
        :order-date="orderDetailData.commandeGroupe?.date_creation || new Date().toISOString()"
        :invoice-id="orderDetailData.id_commande_name"
        @invoice-uploaded="handleInvoiceUploaded"
      />
    </div>

    <!-- Version Mobile/Tablette avec collapse -->
    <div v-if="clientData" class="block md:hidden">
      <!-- Contenu avec transition pour collapse -->
      <div
        :class="[
          'transition-all duration-300 ease-in-out overflow-hidden',
          collapsible ? (isCollapsed ? 'max-h-0 opacity-0' : 'max-h-screen opacity-100') : ''
        ]"
      >
        <div class="space-y-1.5">
          <!-- Numéro de commande -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-black">N° commande:</span>
            <span class="text-sm font-semibold text-black bg-white px-1.5 py-0.5 rounded border">#{{ clientData.orderNumber || '-' }}</span>
          </div>

          <!-- Date -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-black">Date:</span>
            <span class="text-sm text-black">{{ formatDate(clientData.orderDate) }}</span>
          </div>

          <!-- Nom complet -->
          <div class="flex items-start justify-between gap-2">
            <span class="text-sm text-black flex-shrink-0">Client:</span>
            <span class="text-sm text-black text-right leading-tight">{{ clientData.fullName || '-' }}</span>
          </div>

          <!-- Email -->
          <div class="flex items-start justify-between gap-2">
            <span class="text-sm text-black flex-shrink-0">Email:</span>
            <span class="text-sm text-black text-right leading-tight break-all">{{ clientData.email || '-' }}</span>
          </div>

          <!-- SIRET/TVA -->
          <div class="flex items-start justify-between gap-2">
            <span class="text-sm text-black relative" data-siret-tva-info>
              SIRET/TVA:
              <button
                type="button"
                class="inline-flex items-center justify-center w-4 h-4 ml-1 text-[10px] font-bold text-blue-600 border border-blue-300 rounded-full hover:bg-blue-50 transition-colors align-middle"
                @click.stop="showSiretTvaInfo = !showSiretTvaInfo"
              >
                i
              </button>
              <div
                v-if="showSiretTvaInfo"
                class="fixed left-10 right-4 top-24 px-4 py-3 bg-blue-50 border border-blue-200 rounded text-sm text-black z-[9999] shadow-lg md:absolute md:left-auto md:-right-[120px] md:top-auto md:bottom-full md:mb-1 md:max-w-[240px]"
              >
                <div class="space-y-1">
                  <p>
                  Pour le SIRET :
                  <a
                    href="https://avis-situation-sirene.insee.fr/"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:underline"
                  >
                    cliquer ici pour vérifier le SIRET
                  </a>
                  </p>
                  <p>
                  Pour la TVA :
                  <a
                    href="https://ec.europa.eu/taxation_customs/vies/#/vat-validation"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:underline"
                  >
                    cliquer ici pour vérifier la TVA
                  </a>
                  </p>
                </div>
              </div>
            </span>
            <span class="text-sm text-black">{{ clientData.siret || '-' }}</span>
          </div>

          <!-- EORI -->
          <div class="flex items-start justify-between gap-2">
            <span class="text-sm text-black flex-shrink-0">EORI:</span>
            <span class="text-sm text-black text-right leading-tight break-all">{{ clientData.eori || '-' }}</span>
          </div>

          <!-- Mode de paiement -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-black">Paiement:</span>
            <span class="text-sm text-black">{{ clientData.paymentMethod || '-' }}</span>
          </div>
        </div>

        <!-- Adresse de facturation -->
        <div v-if="billingAddress" class="mt-2 pt-2 border-t border-gray-100">
          <h4 class="font-medium text-black mb-1 text-sm">Adresse de facturation</h4>
          <div class="space-y-1 text-sm">
            <div class="flex justify-between">
              <span class="text-black">Société:</span>
              <span class="text-black">{{ billingAddress.enseigne || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Adresse:</span>
              <span class="text-black">{{ billingAddress.adresse || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Code postal:</span>
              <span class="text-black">{{ billingAddress.codePostal || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Ville:</span>
              <span class="text-black">{{ billingAddress.ville || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Pays:</span>
              <span class="text-black">{{ billingAddress.pays || '-' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Téléphone:</span>
              <span class="text-black">{{ billingAddress.telephone || '-' }}</span>
            </div>
          </div>
        </div>
        
        <!-- Zone d'upload de facture - Mobile (dans le collapse) -->
        <div class="mt-2">
          <UploadZone
            v-if="orderDetailData && orderDetailData.id_commande"
            :order-id="Number(orderDetailData.id_commande)"
            :order-group-id="Number(orderDetailData.id_commande_groupe || 0)"
            :order-date="orderDetailData.commandeGroupe?.date_creation || new Date().toISOString()"
            :invoice-id="orderDetailData.id_commande_name"
            @invoice-uploaded="handleInvoiceUploaded"
          />
        </div>
      </div> <!-- Fin de la div de transition -->
    </div>
    <div v-else class="text-sm text-black">
      Chargement des données...
    </div>
  </DataCard>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, type ComputedRef } from 'vue'
import { DataCard } from '@/components'
import UploadZone from './UploadZone.vue'
import type { Address, ClientData, OrderDetailResponse } from '@/types'
import { mapToBillingAddress, mapToClientData } from '@/utils/orderMapper'
import { useAuthStore } from '@/stores/auth'

// Props
interface Props {
  orderId: number
  collapsible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  collapsible: false
})

const emit = defineEmits<{
  invoiceUploaded: [action: 'upload' | 'delete']
  collapsed: [value: boolean]
}>()

// État du collapse
const isCollapsed = ref(true)

// Mode admin (impersonation depuis le back-office EFP): on met en évidence
// le nom de la société de facturation (couleur, taille, gras).
const authStore = useAuthStore()
const isAdmin = computed(() => authStore.isImpersonating)
const showSiretTvaInfo = ref(false)

// Récupérer les données depuis le parent via inject
const orderDetailDataInjected = inject<ComputedRef<{ value: OrderDetailResponse | null }>>('orderDetailData', computed(() => ({ value: null })))

// Simplifier l'accès aux données
const orderDetailData = computed(() => orderDetailDataInjected.value.value)

// Mapper les données client
const clientData = computed<ClientData | null>(() => {
  if (!orderDetailData.value) return null
  return mapToClientData(orderDetailData.value)
})

const billingAddress = computed<Address | null>(() => {
  if (!orderDetailData.value) return null
  return mapToBillingAddress(orderDetailData.value)
})

// Méthodes
const formatDate = (date?: string): string => {
  if (!date) return '-'
  try {
    return new Date(date).toLocaleDateString('fr-FR')
  } catch {
    return date
  }
}

const handleInvoiceUploaded = (action: 'upload' | 'delete') => {
  emit('invoiceUploaded', action)
}

const toggleCollapse = () => {
  if (props.collapsible) {
    isCollapsed.value = !isCollapsed.value
    emit('collapsed', isCollapsed.value)
  }
}

const handleClickOutsideSiretInfo = (event: MouseEvent) => {
  if (!showSiretTvaInfo.value) return
  const target = event.target
  if (!(target instanceof Element)) return

  if (!target.closest('[data-siret-tva-info]')) {
    showSiretTvaInfo.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutsideSiretInfo)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutsideSiretInfo)
})

</script>
